// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineCardCondensedStyles } from "@insite/content-library/Widgets/Cart/CartLineCardCondensed";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LinesHeader from "@tps/App/Components/Cart/LinesHeader";
import VerifyShippingDetailsModal from "@tps/App/Components/Cart/VerifyShippingDetailsModal";
import Notify from "@tps/App/Components/Notify";
import { CartLinesWarehouseStyles } from "@tps/App/SharedStyles/CartLinesWarehouseStyles";
import { CustomAppState } from "@tps/App/Store/Reducers";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@tps/Overrides/Widgets/Cart/CartLineCardExpanded";
import { CustomWarehouseDto, ShippingDetailsModel } from "@tps/XDL/types";
import { parseBoolean } from "@tps/XDL/Utilities";
import * as cssLinter from "css";
import cloneDeep from "lodash/cloneDeep";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showLineNotes = "showLineNotes",
    showImage = "showImage",
    showProductTitle = "showProductTitle",
    showBrand = "showBrand",
    showRemoveAllActions = "showRemoveAllActions",
    showPerUnitPrice = "showPerUnitPrice",
    showPriceLabel = "showPriceLabel",
    showAddToList = "showAddToList",
    showItemCount = "showItemCount",
    showProductNumber = "showProductNumber",
    showBackToTop = "showBackToTop",
    showVmiBinDetails = "showVmiBinDetails",
    customCSS = "customCSS",
    // XNG-Change
    showQtyLabel = "showQtyLabel",
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showLineNotes]: boolean;
        [fields.showImage]: boolean;
        [fields.showProductTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showRemoveAllActions]: boolean;
        [fields.showPerUnitPrice]: boolean;
        [fields.showPriceLabel]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showItemCount]: boolean;
        [fields.showProductNumber]: boolean;
        [fields.showBackToTop]: boolean;
        [fields.showVmiBinDetails]: boolean;
        [fields.customCSS]: string;
        // XNG-Change
        [fields.showQtyLabel]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    cart: getCurrentCartState(state).value,
    promotionsDataView: getCurrentPromotionsDataView(state),
    settingsCollection: getSettingsCollection(state),
    isClearingCart: state.pages.cart.isClearingCart,
    isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
    isLoading: state.data.carts.isLoading,
    isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
    showPricing: state.context.showPricing,
    showInventory: state.context.showInventory,
    allShippingInfo: state.xShippingDetails.warehousesShippingDetails,
    shippingTerms: state.xShippingTerms.data?.values,
    // to stop two spinner issue
    isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
    isCartPage: getCurrentPage(state).type === "CartPage",
    cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

const mapDispatchToProps = {
    clearCurrentCart,
    updateCartLine,
    removeCartLine,
    // XNG-Change
    loadRealTimeInventory,
    loadRealTimePricing,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CartLinesStyles {
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noCartLinesText?: TypographyProps;
    noCartLinesIcon?: IconPresentationProps;
    headerWrapper?: InjectableCss;
    header?: CartLinesHeaderStyles;
    cardList?: CardListStyles;
    cardContainer?: CardContainerStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    footerWrapper?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
    // <xng-change
    cartLinesWarehouseStyles?: CartLinesWarehouseStyles;
    notify?: InjectableCss;
    shippingCommentStyle?: SmallHeadingAndTextStyles;
}

interface CartLinesHeaderStyles {
    itemCountText?: TypographyProps;
    condensedViewCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedViewCheckbox?: CheckboxPresentationProps;
    overflowMenu?: OverflowMenuPresentationProps;
    removeAllLink?: LinkPresentationProps;
    mainSection?: InjectableCss;
    warningIcon?: IconPresentationProps;
    warningSection?: InjectableCss;
    warningText?: InjectableCss;
}

export const cartLinesStyles: CartLinesStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    noCartLinesText: {
        variant: "h3",
    },
    noCartLinesIcon: {
        src: ShoppingCart,
        size: 45,
        color: "text.link",
        css: css`
            margin-bottom: 20px;
        `,
    },
    headerWrapper: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding-bottom: 10px;
        `,
    },
    header: {
        condensedViewCheckboxGroup: {
            css: css`
                margin-left: auto;
            `,
        },
        condensedViewCheckbox: {
            typographyProps: {
                css: css`
                    margin-left: 10px;
                `,
            },
        },
        overflowMenu: {
            cssOverrides: {
                wrapper: css`
                    margin-left: 20px;
                `,
            },
        },
        removeAllLink: {
            css: css`
                margin-left: 24px;
            `,
        },
        mainSection: {
            css: css`
                display: flex;
                align-items: center;
                padding-bottom: 5px;
            `,
        },
        warningIcon: {
            src: "AlertCircle",
            color: "danger.main",
            css: css`
                margin-right: 10px;
            `,
        },
        warningSection: {
            css: css`
                display: flex;
                align-items: center;
                border: 2px solid ${getColor("danger.main")};
                padding: 15px 10px;
            `,
        },
    },
    footerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    backToTopButton: {
        variant: "secondary",
        css: css`
            margin: 16px 0;
        `,
    },
};

const StyledSection = getStyledWrapper("section");

const styles = cartLinesStyles;

export interface Warehouse {
    id: string;
    approximateWeight: string;
    extendedPriceWithDiscountPerWarehouse: string;
    qtySubTotal: string;
    description: string;
    isVerified: boolean;
    shipVia: string;
    shippingMethod: string;
    shippingTerms: string;
    // XNG-Changes
    notes: string;
}

const CartLines: FC<Props> = ({
    cart,
    promotionsDataView,
    settingsCollection,
    clearCurrentCart,
    fields,
    isClearingCart,
    isUpdatingCartLine,
    isLoading,
    updateCartLine,
    removeCartLine,
    isRemovingCartLine,
    showPricing,
    showInventory,
    // XNG-Change
    loadRealTimeInventory,
    allShippingInfo,
    shippingTerms,
    isPreloadingData,
    isCartPage,
    cartPageUrl,
    loadRealTimePricing,
    isCheckoutReviewAndSubmitPage,
}) => {
    const [isCondensed, setIsCondensed] = useState(false);
    const [warehouses, setWarehouses] = useState([] as Warehouse[]);
    const [updatedCartLines, setUpdatedCartLines] = useState<CartLineModel[]>(cart?.cartLines || []);
    const [shippingVerified, setShippingVerified] = useState<{ [warehouseId: string]: boolean }>({});
    // XNG-Change
    const [shippingInformation, setShippingInformation] = useState<{
        [warehouseId: string]: { shipVia: string; shippingMethod: string; shippingTerms: string; notes: string };
    }>({}); // this is from store - if this doesn't exist. will take values from cart

    const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [selectedWarehouse, setSelectedWarehouse] = useState("");
    const dispatch = useDispatch();

    const verifyShippingChangeHandler = (value: boolean, warehouseId: string) => {
        setSelectedWarehouse(warehouseId);
        setIsShippingModalOpen(true);
    };

    React.useEffect(() => {
        const newShippingVerifiedObject: { [warehouseId: string]: boolean } = {};
        // XNG-Change
        const shippingInfo: {
            [warehouseId: string]: { shipVia: string; shippingMethod: string; shippingTerms: string; notes: string };
        } = {};

        const shippingTermDescriptionMap = shippingTerms?.reduce((acc, term) => {
            if (term.name && term.description) {
                acc[term.name] = term.description;
            }
            return acc;
        }, {} as { [key: string]: string });
        for (const warehouseId in allShippingInfo) {
            // if isVerified or has shipViaId, then its verified
            if (allShippingInfo[warehouseId]?.isVerified || allShippingInfo[warehouseId]?.shipViaId) {
                newShippingVerifiedObject[warehouseId] = true;
            }

            if (!isCartPage && cart && allShippingInfo[warehouseId]) {
                // WE need to build the object for shipping information
                const shipVia = cart.carriers?.find(c => c.id === allShippingInfo[warehouseId]?.shipViaId);
                const shippingMethodDescription = shipVia?.shipVias!.find(
                    s => s.id === allShippingInfo[warehouseId]?.shippingMethod,
                )?.description;
                // XNG-Change
                let shippingTermsDescription = "";
                const warehouseShippingInfo = allShippingInfo[warehouseId];
                if (shippingTermDescriptionMap && warehouseShippingInfo && warehouseShippingInfo.termsType) {
                    shippingTermsDescription = shippingTermDescriptionMap[warehouseShippingInfo.termsType];
                } else if (warehouseShippingInfo) {
                    shippingTermsDescription = warehouseShippingInfo.termsType || "";
                }
                shippingInfo[warehouseId] = {
                    shipVia: shipVia?.description || "",
                    shippingMethod: shippingMethodDescription || "",
                    shippingTerms: shippingTermsDescription,
                    // XNG-Change
                    notes: allShippingInfo[warehouseId]?.notes || "",
                };
            }
        }
        if (!isCartPage && cart) {
            setShippingInformation(shippingInfo);
        }
        setShippingVerified(newShippingVerifiedObject);
    }, [allShippingInfo]);

    React.useEffect(() => {
        const warehouses: Warehouse[] = [];
        let warehousesObj: SafeDictionary<{
            Description: string;
            IsVerified: boolean;
            ShipVia: string;
            ShippingMethod: string;
            ShippingTerms: string;
            // XNG-Changes
            Notes: string;
        }> = {};
        let approximateWeightObj: SafeDictionary<string> = {};
        let extendedPriceWithDiscountPerWarehouseObj: SafeDictionary<string> = {};
        let qtySubTotalObj: SafeDictionary<string> = {};

        if (cart?.properties?.warehouses) {
            warehousesObj = JSON.parse(cart?.properties?.warehouses);
        }
        if (cart?.properties?.approximateWeight) {
            approximateWeightObj = JSON.parse(cart?.properties?.approximateWeight);
        }
        if (cart?.properties?.extendedPriceWithDiscountPerWarehouse) {
            extendedPriceWithDiscountPerWarehouseObj = JSON.parse(
                cart?.properties?.extendedPriceWithDiscountPerWarehouse,
            );
        }
        if (cart?.properties?.qtySubTotal) {
            qtySubTotalObj = JSON.parse(cart?.properties?.qtySubTotal);
        }

        const keys = Object.keys(warehousesObj);
        const newShippingVerifiedObject: { [warehouseId: string]: boolean } = {};
        const warehousesShippingDetails: { [warehouseId: string]: ShippingDetailsModel | null } = {};
        keys.forEach(key => {
            const warehouse: Warehouse = {
                id: key,
                approximateWeight: approximateWeightObj[key] || "",
                extendedPriceWithDiscountPerWarehouse: extendedPriceWithDiscountPerWarehouseObj[key] || "",
                qtySubTotal: qtySubTotalObj[key] || "",
                description: warehousesObj[key]?.Description || "",
                isVerified: warehousesObj[key]?.IsVerified || false,
                shipVia: warehousesObj[key]?.ShipVia || "",
                shippingMethod: warehousesObj[key]?.ShippingMethod || "",
                shippingTerms: warehousesObj[key]?.ShippingTerms || "",
                // XNG-Change
                notes: warehousesObj[key]?.Notes || "",
            };
            if (isCartPage && isFirstLoad) {
                newShippingVerifiedObject[key] = warehousesObj[key]?.IsVerified || false;
                if (warehousesObj[key]?.IsVerified) {
                    warehousesShippingDetails[key] = { isVerified: true, warehouseId: key };
                }
            }
            warehouses.push(warehouse);
        });
        setWarehouses(warehouses);
        if (isFirstLoad && isCartPage && cart?.properties?.warehouses) {
            setIsFirstLoad(false);
            setShippingVerified(newShippingVerifiedObject);
            // set shippingDetails initial state
            dispatch({ type: "xShippingDetails/SetInitialShippingDetailsFromCart", warehousesShippingDetails });
        }
    }, [cart?.properties]);

    React.useEffect(() => {
        if (cart && cart.cartLines && cart.cartLines.length && cart.cartLines.some(o => o.productId)) {
            // load inventory and inject it
            const productIds = cart.cartLines.filter(o => o.productId).map(o => o.productId) as string[];
            const filteredProductIds: string[] = [];
            productIds?.forEach(productId => {
                if (filteredProductIds?.indexOf(productId) === -1) {
                    filteredProductIds.push(productId);
                }
            });

            const lines: CartLineModel[] = cloneDeep(cart.cartLines);
            if (!lines) {
                setUpdatedCartLines([]);
                dispatch({ type: "xCart/SetCartHasProductWithInvalidQty", hasProductWithInvalidQty: false } as any);
                return;
            }
            // TPS-789 : Call realtime price before inventory, so that inventory will be cached
            const productsInfos: ProductInfo[] = [];
            cart.cartLines.forEach(cartLine => {
                productsInfos.push({
                    productId: cartLine.productId || "",
                    qtyOrdered: cartLine.qtyOrdered || 0,
                    unitOfMeasure: cartLine.unitOfMeasure,
                    productDetailPath: cartLine.productUri,
                });
            });
            loadRealTimePricing({
                productPriceParameters: productsInfos,
                onComplete(realTimePricingProps) {
                    loadRealTimeInventory({
                        productIds: filteredProductIds,
                        onSuccess: realTimeInventory => {
                            let hasProductWithInvalidQty = false;
                            if (realTimeInventory.realTimeInventoryResults) {
                                realTimeInventory.realTimeInventoryResults?.forEach(inventory => {
                                    lines?.forEach(cartLine => {
                                        if (cartLine.productId === inventory.productId) {
                                            if (!cartLine.properties) {
                                                cartLine.properties = {};
                                            }
                                            (cartLine as any).properties.qtyOnAllBranches = inventory.qtyOnHand;

                                            const warehouseDtos =
                                                (inventory?.inventoryWarehousesDtos?.find(
                                                    o =>
                                                        o.unitOfMeasure.toLowerCase() ===
                                                        (cartLine.unitOfMeasure?.toLowerCase() || ""),
                                                )?.warehouseDtos as CustomWarehouseDto[]) || undefined;

                                            const warehouseDto = warehouseDtos?.find(
                                                warehouseDto =>
                                                    warehouseDto?.properties?.warehouseId ===
                                                    cartLine.properties.warehouseId,
                                            );

                                            (cartLine as any).properties.qtyOnCurrentBrach = warehouseDto?.qty;
                                            if (!parseBoolean(warehouseDto?.properties.speedability)) {
                                                // if speedability is false for one of warehouses, then we need to display requested date field in checkout
                                                dispatch({
                                                    type: "xCheckout/SetShouldDisplayRequestedDateField",
                                                    shouldDisplayRequestedDateField: true,
                                                });
                                            }

                                            if (
                                                (cartLine.qtyOrdered || 1) >
                                                (cartLine as any).properties.qtyOnCurrentBrach
                                            ) {
                                                hasProductWithInvalidQty = true;
                                            }
                                        }
                                    });
                                });
                            }
                            dispatch({
                                type: "xCart/SetCartHasProductWithInvalidQty",
                                hasProductWithInvalidQty,
                            } as any);
                            setUpdatedCartLines(lines);
                        },
                        onError: () => {
                            setUpdatedCartLines(lines);
                        },
                    });
                },
            });
        }
    }, [cart?.cartLines]);

    // XNG-Change: if one of warehouses are not verified, we need to disable submit order button
    React.useEffect(() => {
        if (isCheckoutReviewAndSubmitPage && warehouses && warehouses.length > 0) {
            const notVerifiedWarehouse = warehouses.find(
                warehouse =>
                    !warehouse.shipVia &&
                    ((Object.keys(shippingInformation) && !shippingInformation[warehouse.id]?.shipVia) ||
                        !Object.keys(shippingInformation)),
            );
            if (notVerifiedWarehouse) {
                dispatch({ type: "xCheckout/SetIsSubmitOrderButtonDisabled", isSubmitOrderButtonDisabled: true });
            } else {
                dispatch({ type: "xCheckout/SetIsSubmitOrderButtonDisabled", isSubmitOrderButtonDisabled: false });
            }
        }
    }, [shippingInformation, warehouses, isCheckoutReviewAndSubmitPage]);

    if (isPreloadingData) {
        return null;
    }

    if (isCartPage && (!cart || (cart.lineCount && !cart.cartLines) || isClearingCart)) {
        return (
            <StyledSection {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledSection>
        );
    }

    // const cartLines = cart.cartLines || [];
    const cartLines = updatedCartLines || [];

    if (!cart || !cart.cartLines || cart?.cartLines.length === 0 || cartLines.length === 0) {
        if (!isCartPage) {
            return null;
        }
        return (
            <StyledSection {...styles.centeringWrapper}>
                <Icon {...styles.noCartLinesIcon} />
                <Typography {...styles.noCartLinesText} data-test-selector="cart_noCartLinesMessage">
                    {siteMessage("Cart_NoOrderLines")}
                </Typography>
            </StyledSection>
        );
    }

    const isCondensedChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsCondensed(value);
    };

    const removeAllClickHandler = () => {
        clearCurrentCart();
    };

    const { productSettings } = settingsCollection;
    const { value: promotions } = promotionsDataView;
    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive || o.cantBuy);
    const isLoadingCart = isLoading[cart.id];

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledSection {...styles.wrapper}>
                <LoadingOverlay
                    {...styles.loadingOverlay}
                    loading={
                        isCartPage &&
                        (isLoadingCart || isUpdatingCartLine || !promotionsDataView.value || isRemovingCartLine)
                    }
                >
                    {isCartPage && (
                        <CartLinesHeader
                            totalItemCount={cartLines.length}
                            isCondensed={false}
                            productsCannotBePurchased={productsCannotBePurchased}
                            cartNotPriced={cart.cartNotPriced}
                            hasInsufficientInventory={
                                cart.hasInsufficientInventory && cart.fulfillmentMethod === "Ship"
                            }
                            hasInvalidPrice={hasProductsWithInvalidPrice(cart)}
                            failedToGetRealTimeInventory={cart.failedToGetRealTimeInventory}
                            onIsCondensedChange={isCondensedChangeHandler}
                            onRemoveAllClick={removeAllClickHandler}
                            showItemCount={fields.showItemCount}
                            showRemoveAllActions={fields.showRemoveAllActions}
                        />
                    )}

                    {warehouses.map(warehouse => {
                        return (
                            <StyledSection
                                key={warehouse.id}
                                className="cartlines-warehouse-container"
                                {...styles.cartLinesWarehouseStyles?.warehouseGroup}
                            >
                                <LinesHeader leftTitle={`Branch: ${warehouse.description}`} />
                                {cartLines.map(cartLine => {
                                    if (
                                        (!showPricing && cartLine.isPromotionItem) ||
                                        cartLine.properties.warehouseId !== warehouse.id
                                    ) {
                                        return;
                                    }

                                    const matchingPromotions = promotions
                                        ? promotions.filter(promo => promo.orderLineId === cartLine.id)
                                        : [];
                                    const showRemoveAction = !cartLine.isPromotionItem && cart.canModifyOrder;
                                    return (
                                        <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                                            <CartLineCardExpanded
                                                cart={cart}
                                                promotions={matchingPromotions}
                                                productSettings={productSettings}
                                                showInventoryAvailability={showInventory}
                                                showLineNotes={fields.showLineNotes}
                                                showImage={fields.showImage}
                                                showProductTitle={fields.showProductTitle}
                                                showBrand={fields.showBrand}
                                                showPerUnitPrice={fields.showPerUnitPrice}
                                                showPriceLabel={fields.showPriceLabel}
                                                showQtyLabel={fields.showQtyLabel}
                                                showAddToList={fields.showAddToList}
                                                showProductNumber={fields.showProductNumber}
                                                extendedStyles={styles.cardExpanded}
                                                updateCartLine={updateCartLine}
                                                removeCartLine={removeCartLine}
                                                showRemoveAction={showRemoveAction && isCartPage}
                                                showVmiBinDetails={fields.showVmiBinDetails}
                                                showDistributeOrderButton={isCartPage}
                                                showAvailableQty={true}
                                                editable={!!isCartPage}
                                            />
                                        </CartLineContext.Provider>
                                    );
                                })}
                                <StyledWrapper {...styles.cartLinesWarehouseStyles?.warehouseFooter}>
                                    {isCartPage ? (
                                        <>
                                            <Checkbox
                                                {...styles.cartLinesWarehouseStyles?.verifyShippingCheckbox}
                                                onChange={(e, value) =>
                                                    verifyShippingChangeHandler(value, warehouse.id)
                                                }
                                                checked={shippingVerified[warehouse.id]}
                                                key={warehouse.id}
                                                data-test-selector="verifyShipping"
                                            >
                                                {translate("Verify Shipping Details")}
                                            </Checkbox>
                                            {isShippingModalOpen && selectedWarehouse === warehouse.id && (
                                                <VerifyShippingDetailsModal
                                                    isOpen={isShippingModalOpen}
                                                    setSelectedWarehouse={setSelectedWarehouse}
                                                    setIsOpen={setIsShippingModalOpen}
                                                    warehouse={warehouse}
                                                />
                                            )}
                                        </>
                                    ) : (Object.keys(shippingInformation) &&
                                          shippingInformation[warehouse.id]?.shipVia) ||
                                      warehouse.shipVia ? (
                                        <StyledWrapper {...styles.cartLinesWarehouseStyles?.shippingDetailsSection}>
                                            <Typography {...styles.cartLinesWarehouseStyles?.shippingDetailsTitle}>
                                                {translate("Shipping Details:")}
                                            </Typography>
                                            {Object.keys(shippingInformation) && (
                                                <StyledWrapper
                                                    {...styles.cartLinesWarehouseStyles?.selectedOptionsWrapper}
                                                >
                                                    {(shippingInformation[warehouse.id]?.shipVia ||
                                                        warehouse.shipVia) && (
                                                        <SmallHeadingAndText
                                                            {...styles.cartLinesWarehouseStyles?.shippingOptionStyle}
                                                            heading={translate("Ship Via:")}
                                                            text={
                                                                shippingInformation[warehouse.id]?.shipVia ||
                                                                warehouse.shipVia
                                                            }
                                                        ></SmallHeadingAndText>
                                                    )}

                                                    {(shippingInformation[warehouse.id]?.shippingMethod ||
                                                        warehouse.shippingMethod) && (
                                                        <SmallHeadingAndText
                                                            {...styles.cartLinesWarehouseStyles?.shippingOptionStyle}
                                                            heading={translate("Shipping Method:")}
                                                            text={
                                                                shippingInformation[warehouse.id]?.shippingMethod ||
                                                                warehouse.shippingMethod
                                                            }
                                                        ></SmallHeadingAndText>
                                                    )}

                                                    {((shippingInformation[warehouse.id]?.shippingTerms &&
                                                        shippingInformation[warehouse.id]?.shipVia) ||
                                                        warehouse.shippingTerms) && (
                                                        <SmallHeadingAndText
                                                            {...styles.cartLinesWarehouseStyles?.shippingOptionStyle}
                                                            heading={translate("Shipping Terms:")}
                                                            text={
                                                                shippingInformation[warehouse.id]?.shipVia
                                                                    ? shippingInformation[warehouse.id]?.shippingTerms
                                                                    : warehouse.shippingTerms
                                                            }
                                                        ></SmallHeadingAndText>
                                                    )}
                                                </StyledWrapper>
                                            )}
                                            {/* XNG-Changes */}
                                            {(shippingInformation[warehouse.id]?.notes || warehouse.notes) && (
                                                <SmallHeadingAndText
                                                    {...styles.shippingCommentStyle}
                                                    heading={translate("Shipping Comment:")}
                                                    text={shippingInformation[warehouse.id]?.notes || warehouse.notes}
                                                ></SmallHeadingAndText>
                                            )}
                                            <Link
                                                {...styles.cartLinesWarehouseStyles?.editOptionLink}
                                                href={cartPageUrl}
                                            >
                                                {translate("Edit Option")}
                                            </Link>
                                        </StyledWrapper>
                                    ) : (
                                        <Typography weight="bold" size={12} color="danger">
                                            {translate("Shipping details is not verified")}
                                        </Typography>
                                    )}

                                    <StyledWrapper {...styles.cartLinesWarehouseStyles?.warehouseSubTotalWeightWrapper}>
                                        <Typography {...styles.cartLinesWarehouseStyles?.warehouseSubTotalTypo}>
                                            {siteMessage("Custom_Warehouse_Total", warehouse.qtySubTotal)} 
                                            <b>{warehouse.extendedPriceWithDiscountPerWarehouse}</b>
                                        </Typography>
                                        <Typography {...styles.cartLinesWarehouseStyles?.warehouseWeightTypo}>
                                            {translate("Approximate Weight")}:
                                            <Typography {...styles.cartLinesWarehouseStyles?.unit}>
                                                &nbsp;{warehouse.approximateWeight}&nbsp;
                                                {siteMessage("Custom_Weight_Unit")}
                                            </Typography>
                                        </Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledSection>
                        );
                    })}

                    {fields.showBackToTop !== false && <CartLinesFooter />}
                </LoadingOverlay>
            </StyledSection>
        </StyledWrapper>
    );
};

interface CartLinesHeaderProps {
    totalItemCount: number;
    isCondensed: boolean;
    productsCannotBePurchased?: boolean;
    cartNotPriced?: boolean;
    hasInsufficientInventory?: boolean;
    hasInvalidPrice?: boolean;
    failedToGetRealTimeInventory?: boolean;
    onIsCondensedChange: CheckboxProps["onChange"];
    onRemoveAllClick: () => void;
    showItemCount?: boolean;
    showRemoveAllActions?: boolean;
}

const CartLinesHeader: FC<CartLinesHeaderProps> = ({
    totalItemCount,
    isCondensed,
    productsCannotBePurchased,
    cartNotPriced,
    hasInsufficientInventory,
    hasInvalidPrice,
    failedToGetRealTimeInventory,
    onIsCondensedChange,
    onRemoveAllClick,
    showItemCount = true,
    showRemoveAllActions = true,
}) => {
    const headerStyles = styles.header || {};

    return (
        <StyledSection {...styles.headerWrapper} id="cartHeader">
            {showRemoveAllActions && (
                <StyledSection {...headerStyles.mainSection}>
                    <Link
                        onClick={onRemoveAllClick}
                        {...headerStyles.removeAllLink}
                        data-test-selector="cartlineHeader_removeAll"
                    >
                        {translate("Remove All")}
                    </Link>
                </StyledSection>
            )}
            {productsCannotBePurchased && (
                <Notify
                    {...styles.notify}
                    type="danger"
                    data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                >
                    {siteMessage("Cart_ProductsCannotBePurchased")}
                </Notify>
            )}
            {cartNotPriced && (
                <Notify {...styles.notify} type="danger" data-test-selector="cartlineHeader_NoPriceAvailableAtCheckout">
                    {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                </Notify>
            )}
            {hasInsufficientInventory && (
                <Notify
                    {...styles.notify}
                    type="danger"
                    data-test-selector="cartlineHeader_warningInsufficientInventoryAtCheckout"
                >
                    {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                </Notify>
            )}
            {!cartNotPriced && hasInvalidPrice && (
                <Notify {...styles.notify} type="danger">
                    {siteMessage("Cart_InvalidPriceAtCheckout")}
                </Notify>
            )}
            {failedToGetRealTimeInventory && (
                <Notify {...styles.notify} type="danger">
                    {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                </Notify>
            )}
        </StyledSection>
    );
};

const CartLinesFooter: FC = () => {
    const backToTopClickHandler = () => {
        const header = document.getElementById("cartHeader");
        if (header) {
            window.scrollTo({
                top: header.offsetTop,
                left: 0,
                behavior: "smooth",
            });
        }
    };

    return (
        <StyledSection {...styles.footerWrapper}>
            <Button aria-label={translate("Back to Top")} onClick={backToTopClickHandler} {...styles.backToTopButton}>
                {translate("Back to Top")}
            </Button>
        </StyledSection>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartLines),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.showLineNotes,
                displayName: "Show Line Notes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                tooltip: "This setting does not apply to Punchout orders, which will never display Line Notes.",
                tab: basicTab,
                sortOrder: 0,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 1,
            },
            {
                name: fields.showProductTitle,
                displayName: "Product title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 3,
            },
            {
                name: fields.showRemoveAllActions,
                displayName: "Remove all actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 4,
            },
            {
                name: fields.showPerUnitPrice,
                displayName: "Per unit price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 5,
            },
            {
                name: fields.showPriceLabel,
                displayName: "Price label",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 6,
            },
            {
                name: fields.showQtyLabel,
                displayName: "Qty label",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 7,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to list",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 8,
            },
            {
                name: fields.showItemCount,
                displayName: "Item count",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isVisible: () => false,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 9,
            },
            {
                name: fields.showProductNumber,
                displayName: "Product number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 10,
                isVisible: item => false,
            },
            {
                name: fields.showBackToTop,
                displayName: "Back to top",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isVisible: () => false,
                isEnabled: () => false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 11,
            },
            {
                name: fields.showVmiBinDetails,
                displayName: "VMI Bin details",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 12,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
